// CandidateDetailPage.js
import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';

import CandidateFinancials from './CandidateFinancials';
import useCandidate from '../../hooks/useCandidate';
import useCollections, { CollectionModal, CollectionIcon } from '../../hooks/useCollections';

import './MemberDetailPage.css';
import './CandidateDetailPage.css';

/**
 * CandidateDetailPage
 *
 * Profile page for one FEC candidate, mounted at /us/candidate/:candidateId.
 *
 * Keyed on the FEC candidate ID from the start, which is what the financials
 * endpoint already wants - so unlike the member page this needs no bioGuide to
 * FEC mapping and no backfilled column. Everything it renders derives from the
 * URL, so a pasted or shared link behaves exactly like a click-through.
 *
 * Deliberately narrower than MemberDetailPage: candidates have no votes, no
 * sponsored bills and no office contact record, so there are no tabs. The
 * profile block sits above the finance panel and that is the whole page.
 */

const openCapacitorSite = async (link) => {
  try {
    await Browser.open({ url: link });
  } catch (err) {
    console.error('Failed to open link:', err);
  }
};

const formatDate = (inputDate) => {
  if (!inputDate) return null;

  const date = new Date(inputDate);
  if (Number.isNaN(date.getTime())) return null;

  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  const month = utcDate.getMonth() + 1;
  const day = utcDate.getDate();
  const year = utcDate.getFullYear();
  return `${month}-${day}-${year}`;
};

// The FEC publishes names inverted: 'PALMER, GARY'. Flipping it reads better
// as a page heading, but only when there is exactly one comma - some rows
// carry suffixes or organisation names that should be left alone.
const displayName = (name) => {
  if (!name) return '';

  const parts = name.split(',');
  if (parts.length !== 2) return name.trim();

  const [surname, given] = parts.map((part) => part.trim());
  if (!given) return surname;

  return `${given} ${surname}`;
};

function CandidateDetailPage() {
  const { t } = useTranslation('common');
  const { candidateId } = useParams();

  const { candidate, loading, error } = useCandidate(candidateId);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable,
  } = useCollections();

  // Canonical URL for this candidate, and the identity the collection matches
  // on - so the saved/unsaved icon agrees with itself however the page was
  // reached.
  const candidateUrl = `https://news.govglance.org/us/candidate/${candidateId}`;

  const name = displayName(candidate?.name);
  const filingDate = formatDate(candidate?.last_file_date);

  const handleAddToCollection = () => {
    openCollectionModal({
      title: name,
      url: candidateUrl,
      docLink: candidate?.url || '',
      date: new Date().toISOString(),
      // feedName/branch/topic stay English per stored-data convention
      feedName: 'Candidates',
      branch: 'Elections',
      topic: 'Candidates',
    });
  };

  const renderProfile = () => {
    if (loading) {
      return (
        <div className="member-profile-container">
          <Spinner id="spinner" animation="grow" variant="light" />
        </div>
      );
    }

    if (error || !candidate) {
      return (
        <div className="member-profile-container">
          <p className="member-detail-text">
            {t('common:candidateNotFound', 'This candidate could not be found.')}
          </p>
        </div>
      );
    }

    // Built by filtering rather than with fixed separators, so a missing
    // district or party never leaves a dangling dot.
    const subtitleParts = [
      candidate.office_full,
      candidate.state,
      candidate.party_full,
    ].filter(Boolean);

    const isDistrictOffice = candidate.office === 'H';
    const district = candidate.district;

    return (
      <div className={`candidate-profile ${isDesktop ? 'candidate-profile--desktop' : ''}`}>
        <div className="member-name">
          <span>{name}</span>
        </div>

        {subtitleParts.length > 0 && (
          <div className="member-subtitle">
            <span>{subtitleParts.join(' · ')}</span>
          </div>
        )}

        {isDistrictOffice && district && (
          <div className="member-subtitle">
            {t('common:districtLabel', 'District {{district}}', { district })}
          </div>
        )}

        {candidate.incumbent_challenge_full && (
          <div className="member-subtitle">
            <span>{candidate.incumbent_challenge_full}</span>
          </div>
        )}

        {filingDate && (
          <div className="member-subtitle">
            {t('common:lastFilingDate', 'Last filing {{date}}', { date: filingDate })}
          </div>
        )}

        {candidate.url && (
          <div className="member-contact">
            <span
              onClick={(e) => {
                e.preventDefault();
                openCapacitorSite(candidate.url);
              }}
              className="member-link"
            >
              {t('common:fecRecord', 'FEC record')}
            </span>
          </div>
        )}

     
      </div>
    );
  };

  return (
    <div id="feedDisplay" className="member-detail-page">
      <div className="member-page-content">
        {renderProfile()}

        {/* Only rendered once the candidate resolves, so a bad ID shows the
            not-found message rather than an empty finance panel beneath it. */}
        {!loading && !error && candidate && (
          <div className="member-section">
            <div
              className={`member-section-heading ${
                isDesktop ? 'member-section-heading--center' : ''
              }`}
            >
              {t('common:campaignFinance', 'Campaign Finance')}
            </div>

            <CandidateFinancials candidateId={candidateId} />
          </div>
        )}
      </div>

      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />

      <style>{`
        /* Match the Glance column width on the homepage so cards read as cards
           rather than full-width bars on wide desktop screens. */
        .member-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}

export default CandidateDetailPage;